import { RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Load } from '~hooks/useLoad/models';
import { PricingRate, PricingResolution } from '~hooks/usePricing/models';
import { Nullable } from '~types/Nullable';

export type RateSide = 'customer' | 'vendor' | 'driver';

interface RateRow {
  side: RateSide;
  label: string;
  value: number;
  type: Nullable<RateType>;
  resolution: Nullable<PricingResolution>;
}

export const RESOLUTION_CHIP_COLOR: Record<
  PricingResolution,
  'default' | 'info' | 'warning'
> = {
  inherited: 'default',
  matched: 'info',
  manual: 'warning',
  frozen: 'default',
};

export const RATE_VALUE_FIELD_BY_SIDE: Record<
  RateSide,
  'customer_rate_value' | 'vendor_rate_value' | 'driver_rate_value'
> = {
  customer: 'customer_rate_value',
  vendor: 'vendor_rate_value',
  driver: 'driver_rate_value',
};

export const resolutionLabel = (resolution: PricingResolution) => {
  const labels: Record<PricingResolution, string> = {
    inherited: t('loads.pricing.resolution.inherited'),
    matched: t('loads.pricing.resolution.matched'),
    manual: t('loads.pricing.resolution.manual'),
    frozen: t('loads.pricing.resolution.frozen'),
  };

  return labels[resolution] ?? resolution;
};

const fromPricingRate = (rate: Nullable<PricingRate>) => {
  if (!rate || rate.value === null) {
    return null;
  }

  return { value: rate.value, type: rate.type, resolution: rate.resolution };
};

/**
 * Per-side rate, preferring the richer `pricing` object (resolution included)
 * and falling back to the flat `*_rate_value` Load fields. The driver side
 * additionally falls back to the Load's `driver_pricing` record.
 */
export const buildRateRows = (load: Load): RateRow[] => {
  const sides: Array<{
    side: RateSide;
    label: string;
    fromPricing: ReturnType<typeof fromPricingRate>;
    flatValue: Nullable<number>;
    flatType: Nullable<RateType>;
  }> = [
    {
      side: 'customer',
      label: t('loads.pricing.customer'),
      fromPricing: fromPricingRate(load.pricing?.customerRate),
      flatValue: load.customerRateValue,
      flatType: load.customerRateType,
    },
    {
      side: 'vendor',
      label: t('loads.pricing.vendor'),
      fromPricing: fromPricingRate(load.pricing?.vendorRate),
      flatValue: load.vendorRateValue,
      flatType: load.vendorRateType,
    },
    {
      side: 'driver',
      label: t('loads.pricing.driver'),
      fromPricing:
        fromPricingRate(load.pricing?.driverRate) ??
        fromPricingRate(load.driverPricing?.driverRate),
      flatValue: load.driverRateValue,
      flatType: load.driverRateType,
    },
  ];

  return sides.flatMap(({ side, label, fromPricing, flatValue, flatType }) => {
    const resolved =
      fromPricing ??
      (flatValue !== null && flatValue !== undefined
        ? { value: flatValue, type: flatType, resolution: null }
        : null);

    if (!resolved) {
      return [];
    }

    return [{ side, label, ...resolved }];
  });
};
